import type { CameraFeature } from '@tsr/shared-types';

/**
 * Green motion law (#255/#267 — design-council Law 6 as AMENDED 2026-06-11,
 * v1 + v2, founder-directed; recorded in design-council-2026-06-05.md PART III).
 *
 * Amendment v1 (#255) lifts the zoom-tier restriction on live-camera green:
 * live singles AND live clusters render whole-dot LIVE-GREEN and ANIMATE (the
 * green breathe) at EVERY zoom including country. Family rules unchanged:
 * ≤±10% scale, ≥2s period, calm heartbeat, clickable.
 *
 * Amendment v2 (#267) adds the GLOW: every green dot that breathes also
 * carries a soft halo that swells in phase with the breathe — the halo gets
 * the visible amplitude (size + alpha) the ≤±10% dot is denied, so "live"
 * reads at country zoom without breaking the dot's family bound. One
 * heartbeat, two instruments.
 *
 * The RED-YIELD RULE rides both: while any unacked URGENT-RED anomaly is in
 * view, ALL green animation goes still; red owns motion exclusively. Stillness
 * for the halo = FROZEN at its rest register (rest size, rest alpha — it does
 * not vanish; a halo popping in/out would itself be motion). Yield releases
 * when the urgent is acknowledged or leaves view. All-snapshot clusters stay
 * ambient blue, never animated, never haloed (a cluster with any live-family
 * member renders green — #368).
 *
 * This module is pure (no Cesium) so the amended pinned motion test asserts
 * the whole grammar without a renderer:
 *   - `greenBreatheSize` — the single breathe curve singles + clusters share,
 *     with the yield folded in as `still`.
 *   - `greenGlowSize` / `greenGlowAlpha` — the v2 halo swell, same phase.
 *   - `clusterRegister` — which of the three cluster treatments applies.
 *   - `UrgentRedRegistry` — the "is any unacked urgent-red in view" truth the
 *     yield consults. The URGENT-RED dot-anomaly layers (high-risk hotspots /
 *     flood / ice-storm, plus the #359 RL-qualifying CAP urgents that now pulse
 *     as Level-4-equivalent dots) report their unacked markers each render;
 *     MapView's green pulse callbacks read `anyInView` (throttled) per frame.
 *     AMBIENT CAP still never registers — a Severe or acknowledged warning is a
 *     static polygon/glyph, not an URGENT-RED dot; ambient families
 *     (smoke_detected amber, ghosts) never register.
 */

/* ─────────────────────────── The green breathe ─────────────────────────── */

/** Breathe period (seconds) — family rule "≥2s period". */
export const GREEN_BREATHE_PERIOD_S = 2;
/** Breathe amplitude as a fraction of base size — family rule "≤±10% scale". */
export const GREEN_BREATHE_RATIO = 0.1;

/**
 * Pixel size of a breathing green marker at time `tSeconds`: the base size
 * modulated by a calm ±10% sine on a 2 s period. `still: true` (the red-yield
 * rule, or any caller that must freeze motion) returns exactly the base size —
 * a yielded marker is indistinguishable from a static one, frame after frame.
 */
export function greenBreatheSize(baseSizePx: number, tSeconds: number, still: boolean): number {
  if (still) return baseSizePx;
  return (
    baseSizePx *
    (1 + GREEN_BREATHE_RATIO * Math.sin((tSeconds * 2 * Math.PI) / GREEN_BREATHE_PERIOD_S))
  );
}

/* ───────────────────────────── The green glow ──────────────────────────── */
// Law 6 amendment v2 (#267, founder ruling 2026-06-11): every breathing green
// dot also GLOWS — a soft halo behind the dot, swelling on the same 2 s
// heartbeat. The halo carries the visible amplitude (size + alpha) the ≤±10%
// dot is denied. Red-yield freezes it at the rest register below.

/** Halo rest diameter as a multiple of the dot's base size. The sprite is a
 *  radial falloff (glowTexture.ts), so its painted core reads smaller than the
 *  billboard box — 2.4× boxes a halo that VISUALLY rings the dot. */
export const GREEN_GLOW_SIZE_RATIO = 2.4;
/** Halo swell amplitude as a fraction of its rest size (peak = rest × 1.35) —
 *  the deliberately-visible motion the founder ruled, vs the dot's ≤±10%. */
export const GREEN_GLOW_SWELL_RATIO = 0.35;
/** Halo alpha at rest — ALSO the frozen red-yield register (the halo never
 *  vanishes; it goes still at the bottom of the breath). */
export const GREEN_GLOW_ALPHA_REST = 0.14;
/** Halo alpha at the top of the swell. */
export const GREEN_GLOW_ALPHA_PEAK = 0.38;

/** 0→1 swell phase — the SAME sine the breathe rides, normalized, so halo and
 *  dot grow together (one heartbeat). 0 = bottom of the breath (the rest /
 *  red-yield posture), 1 = the peak. */
function glowPhase01(tSeconds: number): number {
  return 0.5 + 0.5 * Math.sin((tSeconds * 2 * Math.PI) / GREEN_BREATHE_PERIOD_S);
}

/**
 * Pixel size of the glow halo behind a green dot of `dotBaseSizePx` at time
 * `tSeconds`. `still: true` (red-yield) freezes it at exactly the rest size —
 * frame after frame — and that rest size is the curve's own minimum, so a
 * yield mid-swell settles to a posture the breathing halo also passes through.
 */
export function greenGlowSize(dotBaseSizePx: number, tSeconds: number, still: boolean): number {
  const rest = dotBaseSizePx * GREEN_GLOW_SIZE_RATIO;
  if (still) return rest;
  return rest * (1 + GREEN_GLOW_SWELL_RATIO * glowPhase01(tSeconds));
}

/**
 * Alpha of the glow halo at time `tSeconds` — rides the same phase as the size
 * swell (brighter as it grows). `still: true` freezes it at the rest alpha.
 */
export function greenGlowAlpha(tSeconds: number, still: boolean): number {
  if (still) return GREEN_GLOW_ALPHA_REST;
  return (
    GREEN_GLOW_ALPHA_REST + (GREEN_GLOW_ALPHA_PEAK - GREEN_GLOW_ALPHA_REST) * glowPhase01(tSeconds)
  );
}

/* ───────────────────────── Camera / cluster family ─────────────────────── */

/** Live cameras (a real stream, currently up) are the LIVE-GREEN family with
 *  motion; everything else renders static. (Moved from MapView so cluster
 *  classification and the single-camera render share one definition.) */
export function isLiveCamera(f: CameraFeature): boolean {
  return f.properties.status === 'live' && f.properties.stream_type !== 'snapshot';
}

/** The TRAFFIC / snapshot family — the complement of the live-VIDEO family. This
 *  is the cluster FAMILY BOUNDARY (#385): a snapshot camera clusters only with
 *  other snapshots (→ ambient blue), a live-family camera (a real stream, up OR
 *  degraded) clusters only with the live family (→ green). Deliberately the same
 *  `stream_type === 'snapshot'` test {@link clusterRegister} uses to pick
 *  `ambientSnapshot`, and BROADER than {@link isLiveCamera}: a degraded live
 *  stream is NOT `isLiveCamera` but is still live-family, so it must NOT be
 *  bucketed into traffic (that would re-create the very absorption #385 fixes). */
export function isSnapshotCamera(f: CameraFeature): boolean {
  return f.properties.stream_type === 'snapshot';
}

/**
 * Which render treatment a multi-member cluster gets (#255 ruling ii):
 *
 *  - `liveBreathing`     — a live cluster: at least one live-family stream is
 *                          currently UP → whole-dot LIVE-GREEN with the
 *                          governed green breathe. One stale stream — or a
 *                          mixed-in snapshot (#368: green has precedence) —
 *                          never mutes the cluster (the field fleet always
 *                          carries a few degraded members; all-members-up
 *                          would leave the country view mostly still, against
 *                          the ruling's "live clusters animate at every zoom").
 *  - `liveStill`         — at least one live-family stream but NONE currently
 *                          up: green (the family colour) but STATIC — mirrors
 *                          the static green dot a degraded single of the same
 *                          family renders as (LIVE-GREEN motion never claims a
 *                          wholly-down site).
 *  - `ambientSnapshot`   — EVERY member is a snapshot: ambient snapshot blue,
 *                          never animated — the ruling's only blue class, and
 *                          (#368) reached ONLY when no live-family member is
 *                          present. Such all-snapshot clusters are also gated
 *                          to regional zoom and closer by clusterVisibilityFarM.
 */
export type ClusterRegister = 'liveBreathing' | 'liveStill' | 'ambientSnapshot';

export function clusterRegister(members: readonly CameraFeature[]): ClusterRegister {
  // #368 — live-video green ALWAYS takes precedence over traffic-cam blue: a
  // cluster reads ambient-snapshot blue ONLY when EVERY member is a snapshot.
  // Any single live-family member (a real stream — up OR degraded) promotes the
  // whole cluster to green, so a green camera can never hide inside a blue
  // cluster. (Was inverted: ANY one snapshot member forced the cluster blue,
  // burying the green members — the zoom/cluster-breakup symptom.)
  if (members.every(isSnapshotCamera)) return 'ambientSnapshot';
  return members.some(isLiveCamera) ? 'liveBreathing' : 'liveStill';
}

/**
 * #368 — build/z-order for the camera render pass. Every camera entity draws
 * with depth-test disabled, so paint order IS z-order: the entity added LAST
 * paints on top. To guarantee live-video green always sits ABOVE traffic-cam
 * blue — and that a cluster breaking apart on zoom-in never drops its green
 * members under a flood of blue dots — enqueue every ambient-snapshot (blue)
 * cluster FIRST and every live-family (green) cluster LAST, preserving the
 * caller's stable order within each pass (a pure stable partition).
 *
 * Generic over anything carrying `members` (a one-member cluster classifies the
 * same way its single camera colours), so the live-after-snapshot ordering is
 * unit-testable without a Cesium renderer.
 */
export function orderClustersLiveLast<T extends { members: readonly CameraFeature[] }>(
  clusters: readonly T[],
): T[] {
  const ambient: T[] = [];
  const live: T[] = [];
  for (const c of clusters) {
    if (clusterRegister(c.members) === 'ambientSnapshot') ambient.push(c);
    else live.push(c);
  }
  return [...ambient, ...live];
}

/* ─────────────────── Act-over-ambient layer z-order (#410) ───────────────────── *
 *  Icon Council Ruling 1/3 (docs/icon-design-council-2026-06.md) + design-council
 *  §3B.8 act-vs-ambient register. The SAME build-order=z-order law as #368 above,
 *  but at the LAYER (Cesium dataSource) granularity instead of within one camera
 *  pass: every anomaly/asset marker disables the depth test, so the dataSource
 *  added LAST paints on top. To guarantee an URGENT-RED act marker (incident) is
 *  NEVER occluded by an AMBIENT marker (community / asset / sector — the operator's
 *  "community stacked on an urgent alert" pain), install every ambient-register
 *  layer FIRST and every act-register layer LAST. So at a co-located pixel the act
 *  marker is the top-most → it wins the top-most pick and stays clickable.
 *
 *  This is the canonical, unit-testable statement of the precedence that MapView's
 *  layer-install order realizes (the install order IS the build order IS the
 *  z-order); the co-located SPREAD that fans colliding ACT markers apart is the
 *  separate Phase 6b work (Ruling 3).
 * ───────────────────────────────────────────────────────────────────────────── */

/** §3B.8 register a map-marker layer belongs to: `act` = urgent incidents (red,
 *  may pulse), `ambient` = standing context (assets, communities, sectors). */
export type LayerRegister = 'act' | 'ambient';

/**
 * Stable ambient-first / act-last partition for map-marker layers (#410). Mirrors
 * {@link orderClustersLiveLast}: ambient-register layers sort before act-register
 * layers, each group preserving the caller's order. Applied as install/build order,
 * it makes the act register paint ABOVE the ambient register, so an urgent red is
 * never occluded by — and always wins the top-most pick against — a co-located
 * community/asset marker. Generic over anything tagged with a `register`, so the
 * precedence is unit-testable without a Cesium renderer.
 */
export function orderAmbientFirst<T extends { register: LayerRegister }>(layers: readonly T[]): T[] {
  const ambient: T[] = [];
  const act: T[] = [];
  for (const l of layers) {
    if (l.register === 'ambient') ambient.push(l);
    else act.push(l);
  }
  return [...ambient, ...act];
}

/* ───────────────────────── The red-yield registry ──────────────────────── */

/** One unacked URGENT-RED anomaly marker, as the owning layer rendered it. */
export interface UrgentRedMarker {
  lon: number;
  lat: number;
  /** Max eye distance (m) at which the marker's own zoom gate draws it —
   *  REGIONAL_VISIBLE_FAR_M for L1/L2, Infinity for the ungated L3/L4. A
   *  marker tiered out at the current zoom is NOT in view. */
  visibleFarM: number;
}

/** The current view, in the degrees bbox shape MapView already computes. */
export interface UrgentViewState {
  west: number;
  south: number;
  east: number;
  north: number;
  /** Camera eye height (m) — proxies eye-to-marker distance top-down, the
   *  same approximation the markers' own distanceDisplayConditions encode. */
  eyeHeightM: number;
}

/** Longitude containment, tolerant of a view rectangle that wraps the
 *  antimeridian (west > east). */
function lonInRange(lon: number, west: number, east: number): boolean {
  return west <= east ? lon >= west && lon <= east : lon >= west || lon <= east;
}

/**
 * Registry of every unacked URGENT-RED marker each anomaly layer last drew.
 * Layers `report()` on each render (so acks/clears/re-alarms flow through the
 * same poll that recolours them), `setVisible()` on toggle (a hidden layer's
 * urgents are not in view), and `clear()` on destroy.
 */
export class UrgentRedRegistry {
  private layers = new Map<string, { visible: boolean; markers: UrgentRedMarker[] }>();

  /** Replace a layer's unacked-urgent markers (call once per layer render). */
  report(layer: string, markers: readonly UrgentRedMarker[]): void {
    const cur = this.layers.get(layer);
    this.layers.set(layer, { visible: cur?.visible ?? true, markers: [...markers] });
  }

  setVisible(layer: string, visible: boolean): void {
    const cur = this.layers.get(layer);
    this.layers.set(layer, { visible, markers: cur?.markers ?? [] });
  }

  clear(layer: string): void {
    this.layers.delete(layer);
  }

  /** True when any visible layer has an unacked URGENT-RED inside the view
   *  rect that its own zoom gate draws at this eye height — the red-yield
   *  trigger. */
  anyInView(view: UrgentViewState): boolean {
    for (const { visible, markers } of this.layers.values()) {
      if (!visible) continue;
      for (const m of markers) {
        if (view.eyeHeightM > m.visibleFarM) continue; // tiered out at this zoom
        if (m.lat < view.south || m.lat > view.north) continue;
        if (!lonInRange(m.lon, view.west, view.east)) continue;
        return true;
      }
    }
    return false;
  }
}

/** The app-wide registry the anomaly layers report into and MapView's green
 *  pulse callbacks consult. (A module singleton, like the selection bridge —
 *  construct a fresh UrgentRedRegistry in tests instead of resetting this.) */
export const urgentRedRegistry = new UrgentRedRegistry();
